import { useState } from "react";
import { Send, ShieldCheck, UploadCloud, Printer } from "lucide-react";
import { toast } from "sonner";
import { ChapterHeading, Reveal } from "./Reveal";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PROVIDER_IMG =
  "https://images.unsplash.com/photo-1632053002928-1919605ee6f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxwZWRpYXRyaWNpYW4lMjB3aXRoJTIwY2hpbGR8ZW58MHx8fHwxNzg2NzE3NDExfDA&ixlib=rb-4.1.0&q=85";

const EMPTY = {
  practice: "",
  contact_name: "",
  role: "",
  email: "",
  phone: "",
  patient_first_name: "",
  patient_age: "",
  reason: "",
  message: "",
};

export const Referrals = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [doc, setDoc] = useState(null);
  const [hipaa, setHipaa] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!hipaa) {
      toast.error("Please confirm you have authorization to share this referral information.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("hipaa_confirmed", "true");
      if (doc) fd.append("referral_doc", doc);
      await api.post("/referrals", fd);
      toast.success("Thank you — your referral has been sent securely.");
      setForm(EMPTY);
      setDoc(null);
      setHipaa(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="providers" data-testid="providers-section" className="py-24 md:py-32 bg-white/60 border-y border-brand-lightblue/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <ChapterHeading
          number="05"
          eyebrow="Provider Referrals"
          title="A collaborative"
          accent="ABA referral resource."
          testid="providers-heading"
        />

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div className="order-2 lg:order-1">
            <Reveal>
              <p className="text-base md:text-lg leading-relaxed text-brand-muted max-w-xl">
                We welcome relationships with pediatricians, developmental specialists,
                psychiatrists, psychologists, neurologists, therapists, educators, and other
                professionals serving children and families.
              </p>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-brand-muted max-w-xl">
                Clinical ABA services will be supported by experienced{" "}
                <span className="text-brand-ink font-semibold">Board Certified Behavior Analysts (BCBAs)</span>{" "}
                and appropriately credentialed professionals committed to high-quality, ethical,
                family-centered care.
              </p>
              <p className="mt-6 flex items-center gap-2.5 text-base text-brand-ink font-medium" data-testid="referrals-fax">
                <Printer className="h-4 w-4 text-brand-coral shrink-0" />
                Prefer to fax? Send referrals to +1 (832) 201-0421
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-10">
              <div className="relative max-w-md">
                <div className="absolute -bottom-4 -right-4 h-full w-full rounded-[2rem] bg-brand-pale" aria-hidden="true" />
                <img
                  src={PROVIDER_IMG}
                  alt="Pediatric provider with a young child"
                  data-testid="providers-image"
                  className="relative h-72 w-full object-cover rounded-[2rem] shadow-soft"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="order-1 lg:order-2">
            <form
              data-testid="referral-form"
              onSubmit={submit}
              className="rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-lift p-8 md:p-10"
            >
              <h3 className="font-serif text-3xl font-semibold text-brand-blue mb-8">Provider Connection Request</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="ref-practice">Provider / Practice</Label>
                  <Input id="ref-practice" data-testid="referral-practice" required value={form.practice} onChange={set("practice")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ref-contact">Contact Name</Label>
                  <Input id="ref-contact" data-testid="referral-contact-name" required value={form.contact_name} onChange={set("contact_name")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ref-role">Professional Role</Label>
                  <Input id="ref-role" data-testid="referral-role" required value={form.role} onChange={set("role")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ref-email">Email</Label>
                  <Input id="ref-email" data-testid="referral-email" type="email" required value={form.email} onChange={set("email")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ref-phone">Phone</Label>
                  <Input id="ref-phone" data-testid="referral-phone" type="tel" value={form.phone} onChange={set("phone")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label>Reason for Referral</Label>
                  <Select value={form.reason} onValueChange={set("reason")}>
                    <SelectTrigger data-testid="referral-reason" className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12">
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ABA Evaluation">ABA Evaluation</SelectItem>
                      <SelectItem value="Behavior Support">Behavior Support</SelectItem>
                      <SelectItem value="Social Skills">Social Skills</SelectItem>
                      <SelectItem value="School Readiness">School Readiness</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ref-patient-name">Patient First Name (optional)</Label>
                  <Input id="ref-patient-name" data-testid="referral-patient-name" value={form.patient_first_name} onChange={set("patient_first_name")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ref-patient-age">Patient Age (optional)</Label>
                  <Input id="ref-patient-age" data-testid="referral-patient-age" value={form.patient_age} onChange={set("patient_age")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="ref-message">Message</Label>
                  <Textarea id="ref-message" data-testid="referral-message" rows={4} value={form.message} onChange={set("message")} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="ref-doc">Attach Referral Document (optional — PDF, DOC, DOCX, JPG, PNG, max 10 MB)</Label>
                  <label
                    htmlFor="ref-doc"
                    className="flex items-center gap-3 rounded-xl border-2 border-dashed border-brand-lightblue/70 bg-brand-pale/40 px-5 py-5 cursor-pointer transition-colors duration-300 hover:border-brand-coral/60 hover:bg-brand-coralpale/40"
                  >
                    <UploadCloud className="h-6 w-6 text-brand-blue shrink-0" />
                    <span className="text-sm text-brand-muted" data-testid="referral-doc-label">
                      {doc ? doc.name : "Click to attach a referral document"}
                    </span>
                    <input
                      id="ref-doc"
                      data-testid="referral-doc"
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => setDoc(e.target.files?.[0] || null)}
                    />
                  </label>
                </div>
                <div className="sm:col-span-2 flex items-start gap-3 rounded-2xl bg-brand-pale/60 border border-brand-lightblue/50 px-5 py-4" data-testid="referral-privacy-note">
                  <ShieldCheck className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                  <p className="text-sm leading-relaxed text-brand-muted">
                    Patient details and documents are stored securely, never sent over email, and
                    access is limited to authorized Milestone staff. Please share only the minimum
                    necessary information.
                  </p>
                </div>
                <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer" data-testid="referral-hipaa-label">
                  <input
                    type="checkbox"
                    data-testid="referral-hipaa-checkbox"
                    checked={hipaa}
                    onChange={(e) => setHipaa(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-[#3A5A8C]"
                  />
                  <span className="text-sm text-brand-ink leading-relaxed">
                    I confirm I have obtained the appropriate consent/authorization to share this
                    referral information.
                  </span>
                </label>
              </div>
              <button
                type="submit"
                data-testid="referral-submit-button"
                disabled={sending}
                className="group mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-brand-blue text-white font-semibold px-10 py-4 shadow-soft transition-[transform,box-shadow,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:shadow-lift hover:bg-brand-deep disabled:opacity-60 disabled:translate-y-0"
              >
                {sending ? "Sending…" : "Connect With Milestone"}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
